// src/pages/AdminDashboard.jsx
import { useEffect, useState } from "react";
import axios from "axios";
import { FaUserMd, FaUsers, FaCalendarCheck, FaCalendarTimes } from "react-icons/fa";
import { Bar, Pie, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    ArcElement
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Tooltip, Legend, ArcElement);

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const kpiCards = [
    { key: "totalDoctors", label: "Total Doctors", Icon: FaUserMd, accent: "#6366f1", light: "#eef2ff" },
    { key: "totalUsers", label: "Total Patients", Icon: FaUsers, accent: "#0ea5e9", light: "#e0f2fe" },
    { key: "totalAppointments", label: "Appointments", Icon: FaCalendarCheck, accent: "#10b981", light: "#d1fae5" },
    { key: "cancelledAppointments", label: "Cancelled", Icon: FaCalendarTimes, accent: "#f43f5e", light: "#ffe4e6" },
];

const FONT = "'Plus Jakarta Sans', sans-serif";

const tooltipStyle = {
    backgroundColor: "#1e293b",
    titleColor: "#f1f5f9",
    bodyColor: "#94a3b8",
    borderColor: "#334155",
    borderWidth: 1,
    padding: 12,
    cornerRadius: 10,
    titleFont: { family: FONT, weight: "700", size: 13 },
    bodyFont: { family: FONT, size: 12 },
    usePointStyle: true,
    boxWidth: 8, boxHeight: 8,
};

const sharedScales = {
    x: {
        ticks: { color: "#94a3b8", font: { family: FONT, size: 11 }, padding: 6 },
        grid: { display: false },
        border: { display: false },
    },
    y: {
        beginAtZero: true,
        ticks: { color: "#94a3b8", font: { family: FONT, size: 11 }, stepSize: 1, padding: 6 },
        grid: { color: "#f1f5f9" },
        border: { display: false },
    },
};

const legendStyle = {
    position: "top",
    align: "end",
    labels: {
        color: "#64748b",
        font: { family: FONT, size: 12 },
        boxWidth: 8, boxHeight: 8, padding: 16,
        usePointStyle: true, pointStyle: "circle",
    },
};

const sharedOptions = {
    responsive: true,
    animation: { duration: 800, easing: "easeInOutQuart" },
    plugins: { legend: legendStyle, tooltip: tooltipStyle },
    scales: sharedScales,
};

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
* { box-sizing: border-box; }
.adm-wrap {
    background: #f8fafc; min-height: 100vh;
    padding: 2rem 2.5rem;
    font-family: 'Plus Jakarta Sans', sans-serif; color: #1e293b;
}
.adm-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 2rem; }
.adm-header h1 { margin: 0 0 2px 0; font-size: 1.6rem; font-weight: 800; color: #0f172a; letter-spacing: -0.03em; }
.adm-header p { margin: 0; font-size: 0.85rem; color: #94a3b8; }
.adm-date-chip {
    background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
    padding: 7px 14px; font-size: 0.8rem; font-weight: 600; color: #64748b;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

/* KPI */
.adm-kpi-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 1rem; margin-bottom: 1.5rem; }
.adm-kpi {
    background: #fff; border-radius: 16px; padding: 1.4rem 1.25rem;
    border: 1px solid #f1f5f9; box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    display: flex; align-items: center; gap: 1rem;
    transition: box-shadow 0.2s, transform 0.2s;
}
.adm-kpi:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.09); transform: translateY(-2px); }
.adm-kpi-icon { width: 50px; height: 50px; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: var(--light); }
.adm-kpi-label { font-size: 0.75rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 3px; }
.adm-kpi-value { font-size: 20px; font-weight: 800; line-height: 1; letter-spacing: -0.03em; }

/* Cards */
.adm-card {
    background: #fff; border-radius: 20px; padding: 1.5rem;
    border: 1px solid #f1f5f9; box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    margin-bottom: 1.25rem;
}
.adm-card-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1.25rem; }
.adm-card-title { font-size: 0.95rem; font-weight: 700; color: #0f172a; margin: 0; letter-spacing: -0.01em; }
.adm-card-subtitle { font-size: 0.73rem; color: #94a3b8; margin: 3px 0 0 0; font-weight: 400; }
.adm-card-badge {
    font-size: 0.68rem; font-weight: 700; padding: 3px 9px;
    border-radius: 999px; background: #f1f5f9; color: #64748b;
    letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap; flex-shrink: 0;
}

/* Grids */
.adm-grid-2 { display: grid; grid-template-columns: 3fr 2fr; gap: 1.25rem; margin-bottom: 1.25rem; }
.adm-grid-equal { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; margin-bottom: 1.25rem; }

/* Pie */
.adm-pie-wrap { display: flex; align-items: center; justify-content: center; max-height: 220px; }
.adm-pie-legend { display: flex; gap: 1.5rem; justify-content: center; margin-top: 0.75rem; }
.adm-pie-legend-item { display: flex; align-items: center; gap: 6px; font-size: 0.78rem; color: #64748b; font-weight: 500; }
.adm-pie-legend-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }

.adm-section-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #cbd5e1; margin: 2rem 0 0.75rem 0; }

/* Tables */
.adm-table { width: 100%; border-collapse: collapse; }
.adm-table thead th { text-align: left; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; padding: 0 0 0.75rem 0; border-bottom: 1px solid #f1f5f9; }
.adm-table tbody tr { border-bottom: 1px solid #f8fafc; transition: background 0.12s; }
.adm-table tbody tr:last-child { border-bottom: none; }
.adm-table tbody tr:hover { background: #fafbff; }
.adm-table td { padding: 0.85rem 0; font-size: 0.875rem; color: #64748b; vertical-align: middle; }
.adm-table td.primary { color: #0f172a; font-weight: 600; }
.adm-table td.idx { color: #cbd5e1; font-size: 0.78rem; width: 28px; }
.adm-rank { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; background: #f1f5f9; font-size: 0.7rem; font-weight: 700; color: #94a3b8; }
.adm-chip { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 0.75rem; font-weight: 700; }
.adm-chip.blue { background: #eef2ff; color: #6366f1; }
.adm-chip.green { background: #d1fae5; color: #059669; }
.adm-chip.red { background: #ffe4e6; color: #f43f5e; }

@media (max-width: 1024px) {
    .adm-kpi-grid { grid-template-columns: 1fr 1fr; }
    .adm-grid-2, .adm-grid-equal { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
    .adm-header { flex-direction: column; align-items: stretch; text-align: center; gap: 16px; }
    .adm-table { display: block; overflow-x: auto; white-space: nowrap; }
}
@media (max-width: 640px) {
    .adm-wrap { padding: 1rem; }
    .adm-kpi-grid { grid-template-columns: 1fr; }
}
`;

const AdminDashboard = () => {
    const [kpis, setKpis] = useState({});
    const [monthlyData, setMonthlyData] = useState({ labels: [], currentMonth: [], lastMonth: [] });
    const [statusData, setStatusData] = useState({ confirmed: 0, cancelled: 0 });
    const [topDoctors, setTopDoctors] = useState([]);
    const [peakHours, setPeakHours] = useState({ labels: [], data: [] });
    const [newUsersData, setNewUsersData] = useState({ labels: [], data: [] });
    const [mostActivePatients, setMostActivePatients] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);

    useEffect(() => {
        fetchKPIs(); fetchMonthlyComparison(); fetchStatusBreakdown();
        fetchTopDoctors(); fetchPeakHours(); fetchNewUsers();
        fetchMostActivePatients(); fetchRecentActivity();
    }, []);

    const fetchKPIs = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/kpis`);
            setKpis(data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchMonthlyComparison = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/monthly`);
            setMonthlyData({
                labels: data.labels,
                currentMonth: data.currentMonth,
                lastMonth: data.lastMonth,
            });
        } catch (err) {
            console.error(err);
        }
    };

    const fetchStatusBreakdown = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/status`);
            setStatusData(data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchTopDoctors = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/top-doctors`);
            setTopDoctors(data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchPeakHours = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/peak-hours`);
            setPeakHours(data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchNewUsers = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/new-users`);
            setNewUsersData(data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchMostActivePatients = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/most-active-patients`);
            setMostActivePatients(data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchRecentActivity = async () => {
        try {
            const { data } = await axios.get(`${API}/api/dashboard/recent-activity`);
            setRecentActivity(data);
        } catch (err) {
            console.error(err);
        }
    };

    const today = new Date().toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
    const totalAll = (statusData.confirmed || 0) + (statusData.cancelled || 0);

    return (
        <>
            <style>{styles}</style>
            <div className="adm-wrap">

                {/* Header */}
                <div className="adm-header">
                    <div>
                        <h1>Dashboard Overview</h1>
                        <p>Welcome back — here's what's happening today.</p>
                    </div>
                    <div className="adm-date-chip">{today}</div>
                </div>

                {/* KPI Cards */}
                <div className="adm-kpi-grid">
                    {kpiCards.map(({ key, label, Icon, accent, light }) => (
                        <div className="adm-kpi" key={key} style={{ "--light": light }}>
                            <div className="adm-kpi-icon"><Icon size={22} style={{ color: accent }} /></div>
                            <div>
                                <div className="adm-kpi-label">{label}</div>
                                <div className="adm-kpi-value" style={{ color: accent }}>{kpis[key] ?? 0}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <p className="adm-section-label">Analytics</p>

                {/* Row 1: Monthly Bar + Pie */}
                <div className="adm-grid-2">
                    <div className="adm-card">
                        <div className="adm-card-header">
                            <div>
                                <p className="adm-card-title">Monthly Appointments</p>
                                <p className="adm-card-subtitle">Current vs previous month</p>
                            </div>
                            <span className="adm-card-badge">Monthly</span>
                        </div>
                        <Bar
                            data={{
                                labels: monthlyData.labels,
                                datasets: [
                                    { label: "This Month", data: monthlyData.currentMonth, backgroundColor: "#6366f1", borderRadius: { topLeft: 7, topRight: 7 }, borderSkipped: false, barPercentage: 0.6, categoryPercentage: 0.7 },
                                    { label: "Last Month", data: monthlyData.lastMonth, backgroundColor: "#e0e7ff", borderRadius: { topLeft: 7, topRight: 7 }, borderSkipped: false, barPercentage: 0.6, categoryPercentage: 0.7 },
                                ],
                            }}
                            options={sharedOptions}
                        />
                    </div>

                    <div className="adm-card">
                        <div className="adm-card-header">
                            <div>
                                <p className="adm-card-title">Status Breakdown</p>
                                <p className="adm-card-subtitle">All-time outcomes</p>
                            </div>
                            <span className="adm-card-badge">{totalAll} total</span>
                        </div>
                        <div className="adm-pie-wrap">
                            <Pie
                                data={{
                                    labels: ["Confirmed", "Cancelled"],
                                    datasets: [{
                                        data: [statusData.confirmed || 0, statusData.cancelled || 0],
                                        backgroundColor: ["#6366f1", "#fda4af"],
                                        hoverBackgroundColor: ["#4f46e5", "#fb7185"],
                                        borderColor: "#fff",
                                        borderWidth: 4,
                                        hoverOffset: 8,
                                    }],
                                }}
                                options={{
                                    responsive: true,
                                    cutout: "62%",
                                    animation: { animateRotate: true, duration: 900 },
                                    plugins: { legend: { display: false }, tooltip: tooltipStyle },
                                }}
                            />
                        </div>
                        <div className="adm-pie-legend">
                            <div className="adm-pie-legend-item">
                                <div className="adm-pie-legend-dot" style={{ background: "#6366f1" }} />
                                <span>Confirmed ({statusData.confirmed || 0})</span>
                            </div>
                            <div className="adm-pie-legend-item">
                                <div className="adm-pie-legend-dot" style={{ background: "#fda4af" }} />
                                <span>Cancelled ({statusData.cancelled || 0})</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 2: Peak Hours + New Users — side by side, equal width */}
                <div className="adm-grid-equal">
                    {/* Peak Booking Hours */}
                    <div className="adm-card">
                        <div className="adm-card-header">
                            <div>
                                <p className="adm-card-title">Peak Booking Hours</p>
                                <p className="adm-card-subtitle">Appointment volume by hour</p>
                            </div>
                            <span className="adm-card-badge">Hourly</span>
                        </div>
                        <Line
                            data={{
                                labels: peakHours.labels,
                                datasets: [{
                                    label: "Appointments",
                                    data: peakHours.data,
                                    borderColor: "#6366f1",
                                    backgroundColor: "rgba(99,102,241,0.08)",
                                    tension: 0.45,
                                    fill: true,
                                    pointBackgroundColor: "#fff",
                                    pointBorderColor: "#6366f1",
                                    pointBorderWidth: 2.5,
                                    pointRadius: 4,
                                    pointHoverRadius: 6,
                                    pointHoverBackgroundColor: "#6366f1",
                                    pointHoverBorderColor: "#fff",
                                }],
                            }}
                            options={{
                                ...sharedOptions,
                                plugins: { ...sharedOptions.plugins, legend: { display: false } },
                            }}
                        />
                    </div>

                    {/* New Users */}
                    <div className="adm-card">
                        <div className="adm-card-header">
                            <div>
                                <p className="adm-card-title">New User Registrations</p>
                                <p className="adm-card-subtitle">This month vs last month</p>
                            </div>
                            <span className="adm-card-badge">Monthly</span>
                        </div>
                        <Bar
                            data={{
                                labels: newUsersData.labels,
                                datasets: [{
                                    label: "New Users",
                                    data: newUsersData.data,
                                    backgroundColor: ["#6366f1", "#0ea5e9"],
                                    borderRadius: { topLeft: 8, topRight: 8 },
                                    borderSkipped: false,
                                    barPercentage: 0.4,
                                    categoryPercentage: 0.6,
                                }],
                            }}
                            options={{
                                ...sharedOptions,
                                plugins: { ...sharedOptions.plugins, legend: { display: false } },
                            }}
                        />
                    </div>
                </div>

                {/* Tables */}
                <p className="adm-section-label">Leaderboards</p>

                <div className="adm-card">
                    <div className="adm-card-header"><p className="adm-card-title">Top Doctors by Appointments</p></div>
                    <table className="adm-table">
                        <thead><tr><th>#</th><th>Doctor</th><th>Specialization</th><th>Appointments</th></tr></thead>
                        <tbody>
                            {topDoctors.map((doc, index) => (
                                <tr key={doc.id}>
                                    <td className="idx"><span className="adm-rank">{index + 1}</span></td>
                                    <td className="primary">{doc.name}</td>
                                    <td>{doc.specialization}</td>
                                    <td><span className="adm-chip blue">{doc.totalAppointments}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="adm-card">
                    <div className="adm-card-header"><p className="adm-card-title">Most Active Patients</p></div>
                    <table className="adm-table">
                        <thead><tr><th>#</th><th>Patient</th><th>Email</th><th>Appointments</th></tr></thead>
                        <tbody>
                            {mostActivePatients.map((user, index) => (
                                <tr key={user.id}>
                                    <td className="idx"><span className="adm-rank">{index + 1}</span></td>
                                    <td className="primary">{user.name}</td>
                                    <td>{user.email}</td>
                                    <td><span className="adm-chip green">{user.totalAppointments}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="adm-section-label">Activity Log</p>

                <div className="adm-card">
                    <div className="adm-card-header"><p className="adm-card-title">Recent Activity</p></div>
                    <table className="adm-table">
                        <thead>
                            <tr><th>#</th><th>Patient</th><th>Doctor</th><th>Specialization</th><th>Status</th><th>Date</th><th>Time</th></tr>
                        </thead>
                        <tbody>
                            {recentActivity.map((a, index) => (
                                <tr key={a.id}>
                                    <td className="idx"><span className="adm-rank">{index + 1}</span></td>
                                    <td className="primary">{a.patientName}</td>
                                    <td>{a.doctorName}</td>
                                    <td>{a.specialization}</td>
                                    <td>
                                        <span className={`adm-chip ${a.status === "cancelled" ? "red" : "green"}`}>
                                            {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                                        </span>
                                    </td>
                                    <td>{new Date(a.date).toLocaleDateString()}</td>
                                    <td>{a.time}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

            </div>
        </>
    );
};

export default AdminDashboard;